import React from "react";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { CampaignIcon } from "../components/icons";
import SortAscendingDirectionIcon from "../components/icons/SortAscendingDirectionIcon";
import SortDescendingDirectionIcon from "../components/icons/SortDescendingDirectionIcon";
import List from "../components/utils/List";
import Pagination from "../components/utils/Pagination";
import SearchInput from "../components/utils/SearchInput";
import {
  Badge,
  Button,
  ButtonGroup,
  Container,
  EmptyState,
  H6,
  Link,
  ScreenContainer,
  ScreenHeader,
} from "../components/wombat-ui";
import { CampaignMinimalFragment } from "../graphql/fragments";
import useGetCampaigns from "../graphql/hooks/queries/useGetCampaigns";
import { Campaign, SortableVariables } from "../graphql/types";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

type CampaignItemProps = {
  campaign: CampaignMinimalFragment;
};

const CampaignItem: React.FC<CampaignItemProps> = React.memo(({ campaign }) => {
  const t = useTranslations();

  return (
    <li className="py-4" id={`campaignItem.${campaign.id}`}>
      <div className="flex items-center space-x-4">
        <div className="flex-1 min-w-0">
          <Link
            to={`/campaigns/${campaign.slug}`}
            id={`${campaign.id}-link`}
            className="text-sm font-medium truncate"
          >
            {campaign.name}
          </Link>
          <div>
            <span className="text-xs font-normal text-light-900 max-w-paragraph">
              {campaign.description || t("campaign.noDescription")}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap justify-end max-w-lg min-w-0 gap-2">
          <Badge>
            {t(`campaign.madsCount`, { madsCount: campaign.madsCount })}
          </Badge>
        </div>
      </div>
    </li>
  );
});

const CampaignListScreen: React.FC = () => {
  const t = useTranslations();
  const {
    isCapableOfInsertingCampaigns,
    isCapableOfReadingCampaigns,
  } = useViewer();

  const [{ page, q, sortBy, direction }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    q: StringParam,
  });

  const { data: campaignsData, loading } = useGetCampaigns({
    variables: {
      page: page || 1,
      per: 20,
      q: q || undefined,
      sortBy: (sortBy as SortableVariables<Campaign>) || undefined,
      direction: direction as "ASC" | "DESC",
    },
  });

  const campaigns = campaignsData?.campaigns?.collection || [];
  const itemsCount = campaignsData?.campaigns?.metadata?.totalCount;

  return (
    <ScreenContainer
      error={
        isCapableOfReadingCampaigns ? undefined : (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link to="/" variant="button">
                {t("network.backToHome")}
              </Link>
            }
          />
        )
      }
    >
      <ScreenHeader
        title={t("campaign.campaigns") as string}
        description={t("campaign.campaignsDescription") as string}
        icon={CampaignIcon}
        actions={[
          isCapableOfInsertingCampaigns && (
            <Link variant="button" to="/campaigns/new" key="new">
              {t("campaign.createCampaign")}
            </Link>
          ),
        ]}
      />
      <Container className='bg-white'>
        <List
          loading={loading}
          data={campaigns}
          renderAsList={(data) => (
            <CampaignItem campaign={data} key={data.id} />
          )}
          listOnly
          renderEmptyState={() => {
            return <H6>{t("campaign.noMatchingCampaigns")}</H6>;
          }}
          id="users-list"
        >
          <SearchInput
            defaultValue={q}
            onChange={(q) => setQuery({ page: 1, q })}
            label={t("list.search") as string}
            placeholder={t("campaign.searchPlaceholder") as string}
          />
          <div className="flex space-x-3">
            <ButtonGroup scale="sm">
              <Button
                icon={SortAscendingDirectionIcon}
                onClick={() => setQuery({ direction: "ASC" })}
                variant={
                  direction === "ASC" || !direction ? "primary" : undefined
                }
              />
              <Button
                icon={SortDescendingDirectionIcon}
                onClick={() => setQuery({ direction: "DESC" })}
                variant={direction === "DESC" ? "primary" : undefined}
              />
            </ButtonGroup>
          </div>
        </List>
        {!loading && <Pagination itemsCount={itemsCount} itemsPerPage={20} />}
      </Container>
    </ScreenContainer>
  );
};

export default CampaignListScreen;
